import { z } from 'zod'

/** Where the device draws power from (Android `BatteryManager.EXTRA_PLUGGED` + presence). */
export const PLAYER_POWER_SOURCES = ['ac', 'usb', 'wireless', 'dock', 'battery', 'unknown'] as const

export const playerPowerSourceSchema = z.enum(PLAYER_POWER_SOURCES)

export type TPlayerPowerSource = z.infer<typeof playerPowerSourceSchema>

/** Battery percent at or below which an unplugged player is flagged in the dashboard. */
export const PLAYER_LOW_BATTERY_PERCENT = 20 as const

/**
 * A change in reported battery percent smaller than this is not worth a `PLAYERS` fan-out to
 * operators; it is still persisted on the player row so the next refetch shows it.
 */
export const PLAYER_BATTERY_PERCENT_EMIT_DELTA = 5 as const

/** Zod fragments shared by the heartbeat body and the player API row. */
export const playerPowerHeartbeatFieldsSchema = {
  powerSource: playerPowerSourceSchema.optional(),
  batteryPresent: z.boolean().optional(),
  batteryPercent: z.number().int().min(0).max(100).optional(),
  batteryCharging: z.boolean().optional()
}

export interface IPlayerPowerStatus {
  powerSource?: TPlayerPowerSource | null
  batteryPresent?: boolean | null
  batteryPercent?: number | null
  batteryCharging?: boolean | null
}

/** True when the player runs on its own battery at or below {@link PLAYER_LOW_BATTERY_PERCENT}. */
export function isPlayerBatteryLow(status: IPlayerPowerStatus): boolean {
  return status.batteryPresent === true
    && status.powerSource === 'battery'
    && typeof status.batteryPercent === 'number'
    && status.batteryPercent <= PLAYER_LOW_BATTERY_PERCENT
}

/**
 * Whether a new sample should be broadcast to operators: source/presence flips always, percent
 * only when it moved by at least {@link PLAYER_BATTERY_PERCENT_EMIT_DELTA} (or crossed the low mark).
 */
export function shouldEmitPowerChange(prev: IPlayerPowerStatus, next: IPlayerPowerStatus): boolean {
  if (next.powerSource !== undefined && next.powerSource !== prev.powerSource) {
    return true
  }
  if (next.batteryPresent !== undefined && next.batteryPresent !== prev.batteryPresent) {
    return true
  }
  if (typeof next.batteryPercent !== 'number') {
    return false
  }
  if (typeof prev.batteryPercent !== 'number') {
    return true
  }
  if (Math.abs(next.batteryPercent - prev.batteryPercent) >= PLAYER_BATTERY_PERCENT_EMIT_DELTA) {
    return true
  }
  return (prev.batteryPercent > PLAYER_LOW_BATTERY_PERCENT) !== (next.batteryPercent > PLAYER_LOW_BATTERY_PERCENT)
}
